import { useAnimalTimeline, useDeleteAnimalMedia } from '@/api/hooks';
import { AnimalMediaGrid, HistoryTabs } from '@/components/animals/AnimalMediaGrid';
import { MediaLightbox, type MediaLightboxEntry } from '@/components/animals/MediaLightbox';
import { VideoPreviewThumb } from '@/components/animals/VideoPreviewThumb';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatDate } from '@/lib/format';
import { mediaGridPreviewUrl } from '@/lib/mediaPreview';
import { getCachedVideoFrameUrl, getOrExtractVideoPreviewUrl } from '@/lib/videoFrame';
import type { AnimalTimelineDay, AnimalTimelineMediaItem } from '@/types/api';
import {
  ArrowLeftRight,
  CalendarDays,
  ClipboardList,
  History,
  ZoomIn,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

type TabId = 'timeline' | 'media';

export function AnimalTimeline({
  animalId,
  embedded = false,
  view = 'both',
  canDelete = true,
}: {
  animalId: number;
  embedded?: boolean;
  view?: 'both' | TabId;
  canDelete?: boolean;
}) {
  const [tab, setTab] = useState<TabId>('timeline');
  const { data, isLoading } = useAnimalTimeline(animalId);
  const deleteMedia = useDeleteAnimalMedia(animalId);
  const [activeMediaId, setActiveMediaId] = useState<number | null>(null);
  const [activePoster, setActivePoster] = useState<string | undefined>(undefined);
  const activeTab = view === 'both' ? tab : view;

  const timelineMedia = useMemo(
    () => data?.timeline.flatMap((day) => day.media) ?? [],
    [data],
  );
  const activeMedia =
    activeMediaId == null ? null : timelineMedia.find((item) => item.id === activeMediaId) ?? null;

  useEffect(() => {
    if (!activeMedia || activeMedia.media.media_kind !== 'video') {
      setActivePoster(undefined);
      return;
    }

    if (activeMedia.media.thumbnail_url) {
      setActivePoster(activeMedia.media.thumbnail_url);
      return;
    }

    const cached = getCachedVideoFrameUrl(activeMedia.media.url);
    if (cached) {
      setActivePoster(cached);
      return;
    }

    let cancelled = false;
    void getOrExtractVideoPreviewUrl(activeMedia.media.url)
      .then((url) => {
        if (!cancelled) setActivePoster(url);
      })
      .catch(() => {
        if (!cancelled) setActivePoster(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [activeMedia]);

  async function handleDeleteTimelineMedia(item: MediaLightboxEntry) {
    const label = item.media.media_kind === 'video' ? 'este vídeo' : 'esta foto';
    if (!window.confirm(`Remover ${label} da galeria do animal?`)) return;
    await deleteMedia.mutateAsync(item.id);
    setActiveMediaId(null);
  }

  const content = (
    <>
      {view === 'both' && (
        <CardHeader className={embedded ? 'px-0 pt-0' : undefined}>
          <HistoryTabs tab={tab} onChange={setTab} />
        </CardHeader>
      )}
      <CardBody className={embedded ? 'px-0 pb-0' : view !== 'both' ? 'p-0' : undefined}>
        {activeTab === 'media' ? (
          <AnimalMediaGrid animalId={animalId} canDelete={canDelete} />
        ) : isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-manejai-sand/40 animate-pulse" />
            ))}
          </div>
        ) : !data || data.timeline.length === 0 ? (
          <EmptyState
            icon={History}
            title="Histórico vazio"
            description="Capture fotos ou registre eventos para montar a história deste animal."
          />
        ) : (
          <div className="space-y-6">
            {data.timeline.map((day) => (
              <TimelineDay key={day.date} day={day} onMediaClick={(item) => setActiveMediaId(item.id)} />
            ))}
          </div>
        )}
      </CardBody>

      <MediaLightbox
        open={activeMediaId != null}
        onClose={() => setActiveMediaId(null)}
        items={timelineMedia}
        activeId={activeMediaId}
        onActiveChange={setActiveMediaId}
        videoPosterUrl={activePoster}
        actions={
          canDelete && activeMedia
            ? (item) => (
                <button
                  type="button"
                  disabled={deleteMedia.isPending}
                  onClick={() => handleDeleteTimelineMedia(item)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-400/40 px-3 py-1.5 text-sm text-red-200 transition hover:bg-red-500/20 disabled:opacity-50"
                >
                  Remover {item.media.media_kind === 'video' ? 'vídeo' : 'foto'}
                </button>
              )
            : undefined
        }
      />
    </>
  );

  if (embedded || view !== 'both') {
    return <div className={embedded ? 'border-t border-manejai-sand/60 pt-4' : undefined}>{content}</div>;
  }

  return <Card>{content}</Card>;
}

function TimelineDay({
  day,
  onMediaClick,
}: {
  day: AnimalTimelineDay;
  onMediaClick: (item: AnimalTimelineMediaItem) => void;
}) {
  const hasContext = day.events.length > 0 || day.movements.length > 0;

  return (
    <div className="relative pl-6">
      <div className="absolute left-0 top-1.5 h-full w-px bg-manejai-sand" />
      <div className="absolute left-[-5px] top-1.5 h-2.5 w-2.5 rounded-full bg-manejai-sage ring-4 ring-white" />

      <div className="flex items-center gap-2 mb-3">
        <CalendarDays className="h-4 w-4 text-manejai-moss" />
        <h3 className="font-medium text-manejai-forest">{formatDate(day.date)}</h3>
      </div>

      {day.media.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 mb-3">
          {day.media.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onMediaClick(item)}
              className="group relative aspect-square overflow-hidden rounded-xl bg-manejai-ink/5"
              aria-label={item.media.media_kind === 'video' ? 'Ver vídeo' : 'Ampliar foto'}
            >
              {item.media.media_kind === 'video' ? (
                <VideoPreviewThumb
                  videoUrl={item.media.url}
                  thumbnailUrl={item.media.thumbnail_url}
                  alt="Miniatura do vídeo"
                  lazy
                />
              ) : (
                <>
                  <img
                    src={mediaGridPreviewUrl(item.media)}
                    alt="Captura"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition group-hover:opacity-100">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/95 text-manejai-forest shadow-sm">
                      <ZoomIn className="h-4 w-4" />
                    </span>
                  </div>
                </>
              )}
            </button>
          ))}
        </div>
      )}

      {hasContext && (
        <div className="space-y-2">
          {day.events.map((event) => (
            <div
              key={`event-${event.id}`}
              className="flex items-start gap-2 rounded-xl bg-manejai-cream/60 px-3 py-2 text-sm"
            >
              <ClipboardList className="h-4 w-4 text-manejai-moss mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-manejai-forest">{event.label}</p>
                {event.notes && <p className="text-manejai-bark/70 mt-0.5">{event.notes}</p>}
              </div>
            </div>
          ))}
          {day.movements.map((movement) => (
            <div
              key={`movement-${movement.id}`}
              className="flex items-start gap-2 rounded-xl bg-manejai-sand/40 px-3 py-2 text-sm"
            >
              <ArrowLeftRight className="h-4 w-4 text-manejai-moss mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-manejai-forest">{movement.label}</p>
                {movement.notes && <p className="text-manejai-bark/70 mt-0.5">{movement.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {!day.media.length && !hasContext && (
        <p className="text-sm text-manejai-bark/50">Sem registros neste dia.</p>
      )}
    </div>
  );
}
